/**
 * IMPORTS
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { isSameDay } from "date-fns";
import JSZip from "jszip";
import * as XLSX from "xlsx-js-style";

import { countValidImageFilesFromZip } from "@pages/client/details-model/view-model/helpers/contar-arquivos-validos-zip";
import { handleIsCellEmpty } from "@pages/client/details-model/view-model/helpers/is-cell-empty";
import { ordenarLinhaPlanilhaPorPrioridade } from "@pages/client/details-model/view-model/helpers/montar-linha-planilha-avulsa-ordenada";
import type { IMetadadoCampoVariavel } from "@pages/client/details-model/view-model/helpers/normalizar-linhas-planilhas";
import { validarConsistenciaArquivosRemessa } from "@pages/client/details-model/view-model/helpers/validar-consistencia-arquivos-remessa";
import type { ITableData } from "@pages/client/details-model/view-model/interface";

import { useSolicitarRemessa } from "@/hooks/remessas/solicitar-remessa";
import type { ISolicitacaoRemessaPayload } from "@/hooks/remessas/solicitar-remessa/index.d";

import { useStoreZustandModelosCliente } from "@/store-zustand/cliente/details-modelo";
import { useRequestShipmentLoose } from "@/store-zustand/cliente/request-shipment-loose";
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { limparDocumento } from "@/utils/format-document";
import { notifyAlert, notifyError, notifySuccess } from "@/utils/notify-messages";
import { handleSentryLogSolicitacaoRemessa } from "@/utils/sentry/handle-sentry-log-solicitacao-remessa";

const MAX_ZIP_SIZE_MB = 15;
const MAX_ZIP_SIZE_BYTES = MAX_ZIP_SIZE_MB * 1024 * 1024;
const MAX_IMAGE_SIZE_MB = 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

type IDadosResponsabilidade = {
  nome: string;
  documento: string;
};

const useRequestShipmentLooseViewModel = () => {
  const navigate = useNavigate();

  const { user } = useStoreZustandUserAuth();
  const { modeloUnico } = useStoreZustandModelosCliente();

  const {
    dadosRemessa,
    selectedFile,
    selectedZipFile,
    isAvulsa,
    remessaAvulsaLimit,
    isModalSolicitacaoRemessa,
    isOpenTermModal,
    dadosResponsabilidade,
    setDadosRemessa,
    setSelectedFile,
    setSelectedZipFile,
    setIsAvulsa,
    setMaxAvulsoRequestLimitDia,
    setIsModalSolicitacaoRemessa,
    setIsOpenTermModal,
    setDadosResponsabilidade,
    clearPersistedData,
    setCleanStateData,
  } = useRequestShipmentLoose();

  const { solicitarRemessaAsync, isLoadingSolicitarRemessa } = useSolicitarRemessa();

  const obligatory = modeloUnico?.modeloCompleto?.campo_chave ?? "matricula";
  const camposVariaveisRaw = modeloUnico?.modeloCompleto?.campos_variavei_planilhas;
  const camposVariaveisModelo = modeloUnico?.modeloCompleto?.campos_variaveis;

  const metadadosCamposVariaveis: IMetadadoCampoVariavel[] = useMemo(() => {
    if (Array.isArray(camposVariaveisModelo) && camposVariaveisModelo.length > 0) {
      const camposOrdenados = [...camposVariaveisModelo].sort(
        (anterior: { ordem?: number }, proximo: { ordem?: number }) =>
          (anterior?.ordem ?? 0) - (proximo?.ordem ?? 0)
      );

      return camposOrdenados
        .map((campo: { nome?: string; obrigatorio?: number | boolean }) => ({
          nome: typeof campo?.nome === "string" ? campo.nome.trim() : "",
          obrigatorio: Number(campo?.obrigatorio) === 1,
        }))
        .filter((campo) => campo.nome.length > 0);
    }

    if (Array.isArray(camposVariaveisRaw) && camposVariaveisRaw.length > 0) {
      return camposVariaveisRaw
        .filter((nome): nome is string => typeof nome === "string" && nome.trim().length > 0)
        .map((nome) => ({ nome: nome.trim(), obrigatorio: true }));
    }

    return [];
  }, [camposVariaveisModelo, camposVariaveisRaw]);

  const [avulsaMatricula, setAvulsaMatricula] = useState("");
  const [avulsaCampos, setAvulsaCampos] = useState<Record<string, string>>({});
  const [avulsaImagem, setAvulsaImagem] = useState<File | null>(null);
  const [avulsaImagemPreview, setAvulsaImagemPreview] = useState<string | null>(null);

  const avulsaImagemInputRef = useRef<HTMLInputElement>(null);

  const notifyZipTooLarge = (zipFileName: string) => {
    notifyAlert(`Arquivo ZIP ${zipFileName} muito grande (máx ${MAX_ZIP_SIZE_MB}MB)!`);
  };

  const notifyImageTooLarge = (fileName: string) => {
    notifyAlert(
      `A imagem "${fileName}" excede o limite permitido. Cada imagem deve ter no máximo ${MAX_IMAGE_SIZE_MB} MB.`
    );
  };

  const isValidZipSize = (zipFile: Blob) => zipFile.size <= MAX_ZIP_SIZE_BYTES;

  const isValidImageSize = (file: Blob, fileName: string) => {
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      notifyImageTooLarge(fileName);
      return false;
    }
    return true;
  };

  const getOrientationAbbreviation = useCallback((orientation: string): "v" | "h" | null => {
    if (typeof orientation !== "string") return null;
    const value = orientation.toLowerCase();
    if (value === "vertical") return "v";
    if (value === "horizontal") return "h";
    notifyAlert("Orientacao invalida!");
    return null;
  }, []);

  const handleAvulsaImagemSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isValidImageSize(file, file.name)) {
      event.target.value = "";
      return;
    }

    if (avulsaImagemPreview) {
      URL.revokeObjectURL(avulsaImagemPreview);
    }

    setAvulsaImagem(file);
    setAvulsaImagemPreview(URL.createObjectURL(file));
  };

  const handleAvulsaCampoChange = (campo: string, valor: string) => {
    setAvulsaCampos((previous) => ({
      ...previous,
      [campo]: valor,
    }));
  };

  const handleCleanState = useCallback(() => {
    if (avulsaImagemPreview) {
      URL.revokeObjectURL(avulsaImagemPreview);
    }

    setCleanStateData();
    setAvulsaMatricula("");
    setAvulsaCampos({});
    setAvulsaImagem(null);
    setAvulsaImagemPreview(null);
  }, [avulsaImagemPreview, setCleanStateData]);

  const handleAvulsaSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!modeloUnico) {
      notifyError("Selecione um modelo antes de montar a solicitacao avulsa.");
      return;
    }

    if (!avulsaMatricula) {
      notifyError(`Por favor, preencha o campo ${obligatory}!`);
      return;
    }

    for (const metadadoCampo of metadadosCamposVariaveis) {
      if (!metadadoCampo.obrigatorio) continue;
      if (handleIsCellEmpty(avulsaCampos[metadadoCampo.nome])) {
        notifyError(`Por favor, preencha o campo ${metadadoCampo.nome}!`);
        return;
      }
    }

    const foundModelAvulso = remessaAvulsaLimit.filter(
      (item) =>
        item.id === modeloUnico?.id &&
        item.user_id === user?.id &&
        isSameDay(new Date(item.dataCriacao), new Date())
    ).length;

    if (foundModelAvulso >= 2) {
      notifyAlert("Voce atingiu o limite diario de 2 solicitacoes avulsas para este modelo!");
      return;
    }

    const existingIndex = dadosRemessa?.findIndex(
      (row) => String(row[obligatory]).toLowerCase() === avulsaMatricula.toLowerCase()
    );

    const newRow: ITableData = {
      [obligatory]: avulsaMatricula,
    };

    if (metadadosCamposVariaveis.length > 0) {
      metadadosCamposVariaveis.forEach((metadadoCampo) => {
        newRow[metadadoCampo.nome] = avulsaCampos[metadadoCampo.nome] || "";
      });
    }

    if (avulsaImagem && avulsaImagemPreview) {
      if (!isValidImageSize(avulsaImagem, avulsaImagem.name)) {
        return;
      }

      const imageName = avulsaImagem.name.replace(/\.[^/.]+$/, "");
      newRow.foto = imageName;
      newRow.imageUrl = URL.createObjectURL(avulsaImagem);
      newRow.imagePath = avulsaImagem.name;
      newRow.imageSource = "manual";
      newRow.imageComplete = avulsaImagem;
    }

    let updatedRows: ITableData[] = [];
    if (existingIndex !== undefined && existingIndex !== -1 && dadosRemessa) {
      const updated = [...dadosRemessa];
      if (updated[existingIndex]?.imageUrl) {
        URL.revokeObjectURL(String(updated[existingIndex].imageUrl));
      }
      updated[existingIndex] = newRow;
      updatedRows = updated;
      notifySuccess(`Registro de ${avulsaMatricula} atualizado com sucesso!`);
    } else {
      const updated = [...(dadosRemessa ?? []), newRow];
      updatedRows = updated;
      notifySuccess(`Registro de ${avulsaMatricula} adicionado com sucesso!`);
    }

    setDadosRemessa(updatedRows);

    const camposBloqueados = ["foto", "imageUrl", "imagePath", "imageSource", "imageComplete"];
    const cleanedRows = updatedRows.map((row) => {
      const plain: Record<string, unknown> = {};
      Object.keys(row).forEach((key) => {
        if (!camposBloqueados.includes(key)) {
          plain[key] = row[key];
        }
      });
      return ordenarLinhaPlanilhaPorPrioridade(plain, obligatory, metadadosCamposVariaveis);
    });

    const worksheet = XLSX.utils.json_to_sheet(cleanedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dados");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const planilhaFile = new File([excelBuffer], "remessa_avulsa.xlsx", {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    setSelectedFile(planilhaFile);
    const exigeCargaFoto = Number(modeloUnico?.modeloCompleto?.tem_carga_foto) === 1;
    const todasLinhasComImagem =
      updatedRows.length > 0 &&
      updatedRows.every((row) => (row?.imageComplete as File | undefined) instanceof File);

    if (exigeCargaFoto && todasLinhasComImagem) {
      const zip = new JSZip();

      for (const row of updatedRows) {
        const file = row?.imageComplete as File;
        const valorCampoChave = String(row[obligatory] ?? "").trim();

        if (!file || !valorCampoChave) continue;

        if (!isValidImageSize(file, file.name)) {
          return;
        }

        const nomeOriginal = file.name ?? "imagem.jpg";
        const extensaoOriginal = nomeOriginal.split(".").pop()?.toLowerCase();
        const extensaoFinal =
          extensaoOriginal && extensaoOriginal.length > 0 ? extensaoOriginal : "jpg";

        zip.file(`${valorCampoChave}.${extensaoFinal}`, file);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFile = new File([zipBlob], "remessa_avulsa.zip", {
        type: "application/x-zip-compressed",
      });

      if (!isValidZipSize(zipFile)) {
        notifyZipTooLarge(zipFile.name);
        setSelectedZipFile(null);
        return;
      }

      setSelectedZipFile(zipFile);
    } else {
      setSelectedZipFile(null);
    }

    setIsAvulsa(true);

    if (avulsaImagemPreview) {
      URL.revokeObjectURL(avulsaImagemPreview);
    }

    setAvulsaMatricula("");
    setAvulsaCampos({});
    setAvulsaImagem(null);
    setAvulsaImagemPreview(null);
  };

  const submitSolicitarRemessa = useCallback(
    async (dadosResponsabilidadeAtual?: IDadosResponsabilidade) => {
      if (!modeloUnico) {
        notifyError("Selecione um modelo antes de solicitar a remessa.");
        setIsModalSolicitacaoRemessa(false);
        return;
      }

      const dadosResponsavel = dadosResponsabilidadeAtual ?? dadosResponsabilidade;

      if (!dadosResponsavel) {
        setIsOpenTermModal(true);
        setIsModalSolicitacaoRemessa(false);
        return;
      }

      const clientePortal =
        user?.tipo_login === "subordinado" ? user?.cliente_principal?.id : user?.id;
      const idSolicitanteSubordinado = user?.tipo_login === "subordinado" ? user?.id : null;
      const idSolicitanteCliente = user?.tipo_login === "user" ? user?.id : null;

      const totalSolicitacao = dadosRemessa?.length ?? 0;
      const exigeCargaFoto = Number(modeloUnico?.modeloCompleto?.tem_carga_foto) === 1;
      const dataSolicitarRemessa = {
        cliente_id: clientePortal,
        user_id_solicitante_remessa: idSolicitanteCliente,
        users_solicitante_subordinado_id: idSolicitanteSubordinado,
        consultor_id: user?.consultor?.id ?? null,
        modelo_tecnico_id: modeloUnico?.modeloCompleto?.id,
        tecnologia_id: modeloUnico?.modeloCompleto?.tecnologia?.id,
        produto_id: modeloUnico?.modeloCompleto?.produto?.id,
        total_solicitacoes: totalSolicitacao,
        situacao: "solicitado",
        status: "envio_de_dados",
        campo_chave: obligatory,
        posicao: getOrientationAbbreviation(modeloUnico?.modeloCompleto?.posicionamento as string),
        nome: dadosResponsavel.nome,
        documento: limparDocumento(dadosResponsavel.documento),
      } as unknown as ISolicitacaoRemessaPayload;

      if (!dataSolicitarRemessa?.total_solicitacoes) {
        notifyError("Adicione pelo menos uma solicitacao avulsa antes de enviar.");
        setIsModalSolicitacaoRemessa(false);
        return;
      }

      if (exigeCargaFoto && selectedZipFile && !isValidZipSize(selectedZipFile)) {
        notifyZipTooLarge(selectedZipFile.name);
        setSelectedZipFile(null);
        setIsModalSolicitacaoRemessa(false);
        return;
      }

      if (exigeCargaFoto && !selectedZipFile) {
        notifyError("Por favor, selecione o arquivo ZIP para continuar!");
        setIsModalSolicitacaoRemessa(false);
        return;
      }

      const formData = new FormData();
      Object.entries(dataSolicitarRemessa).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value as any);
        }
      });

      if (exigeCargaFoto && selectedZipFile) {
        formData.append("zip_file", selectedZipFile);
      }
      if (selectedFile) {
        formData.append("csv_file", selectedFile);
      }

      if (exigeCargaFoto && (dadosRemessa?.length ?? 0) > 0) {
        const semFoto = (dadosRemessa ?? []).some((row) => {
          const complete = (row as Record<string, unknown>).imageComplete;
          return !row.imageUrl && !complete;
        });

        if (semFoto) {
          notifyError("Associe uma foto a cada registro antes de solicitar a remessa.");
          setIsModalSolicitacaoRemessa(false);
          return;
        }
      }

      const possuiUploadParaValidar = !!selectedFile || (exigeCargaFoto && !!selectedZipFile);
      if (possuiUploadParaValidar) {
        const totalSolicitacoesEnviado = Number(dataSolicitarRemessa?.total_solicitacoes);
        const qtdCsv = dadosRemessa?.length ?? 0;
        const qtdZip =
          exigeCargaFoto && selectedZipFile
            ? await countValidImageFilesFromZip(selectedZipFile)
            : null;

        const resultado = validarConsistenciaArquivosRemessa({
          totalSolicitacoes: totalSolicitacoesEnviado,
          qtdCsv,
          qtdZip,
        });

        if (!resultado.valido) {
          notifyError(resultado.mensagem);
          setIsModalSolicitacaoRemessa(false);
          return;
        }
      }

      try {
        await solicitarRemessaAsync(formData as any, {
          onSuccess: async (response) => {
            const { data } = response;
            setIsModalSolicitacaoRemessa(false);

            if (!data?.id) return;

            const foundModelAvulso = remessaAvulsaLimit.filter(
              (item) => item.id === modeloUnico?.id
            )?.length;

            if (isAvulsa) {
              setMaxAvulsoRequestLimitDia([
                ...remessaAvulsaLimit,
                {
                  id: Number(modeloUnico?.id),
                  user_id: Number(user?.id),
                  maxAvulsoRequestLimitDia: foundModelAvulso + 1,
                  dataCriacao: new Date().toISOString(),
                },
              ]);
            }

            await handleSentryLogSolicitacaoRemessa({
              user,
              payload: dataSolicitarRemessa,
              response: data,
            });

            clearPersistedData();
            handleCleanState();
            navigate("/shipments-in-progress");
          },
        });
      } catch (_error) {
        setIsModalSolicitacaoRemessa(false);
        setDadosResponsabilidade(null);
      }
    },
    [
      modeloUnico,
      dadosResponsabilidade,
      user,
      dadosRemessa,
      obligatory,
      getOrientationAbbreviation,
      selectedZipFile,
      selectedFile,
      solicitarRemessaAsync,
      remessaAvulsaLimit,
      isAvulsa,
      setMaxAvulsoRequestLimitDia,
      clearPersistedData,
      handleCleanState,
      navigate,
      setIsModalSolicitacaoRemessa,
      setIsOpenTermModal,
      setSelectedZipFile,
      setDadosResponsabilidade,
    ]
  );

  const handleConfirmarSolicitacao = () => {
    if (!dadosRemessa || dadosRemessa.length === 0) {
      notifyError("Adicione ao menos uma solicitacao avulsa antes de enviar.");
      return;
    }
    setIsModalSolicitacaoRemessa(true);
  };

  const handleSubmitResponsabilidade = async (dados: IDadosResponsabilidade) => {
    setDadosResponsabilidade(dados);
    setIsOpenTermModal(false);
    await submitSolicitarRemessa(dados);
  };

  const handleSolicitarRemessa = async () => {
    await submitSolicitarRemessa();
  };

  return {
    modeloUnico,
    obligatory,
    metadadosCamposVariaveis,
    dadosRemessa,
    avulsaMatricula,
    avulsaCampos,
    avulsaImagemPreview,
    avulsaImagemInputRef,
    isModalSolicitacaoRemessa,
    isOpenTermModal,
    isLoadingSolicitarRemessa,
    setAvulsaMatricula,
    setAvulsaImagem,
    setAvulsaImagemPreview,
    setIsModalSolicitacaoRemessa,
    setIsOpenTermModal,
    handleAvulsaImagemSelect,
    handleAvulsaCampoChange,
    handleAvulsaSubmit,
    handleConfirmarSolicitacao,
    handleSolicitarRemessa,
    handleSubmitResponsabilidade,
    handleCleanState,
  };
};

export { useRequestShipmentLooseViewModel };
